/**
 * DataGrid Component - AG Grid wrapper with server-side support
 * Supports row selection, selective export, server-side pagination/sorting, and density control
 */
import React, { useCallback, useMemo, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useDataGridDensity, DENSITY_CONFIG } from '../../contexts/DataGridContext';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { 
  AlignVerticalSpaceAround, 
  Check,
  Rows3,
  Rows4,
} from 'lucide-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DataGrid.css';

const AG_GRID_LOCALE_PT_BR = {
  page: 'Página',
  more: 'mais',
  to: 'até',
  of: 'de',
  next: 'Próximo',
  last: 'Último',
  first: 'Primeiro',
  previous: 'Anterior',
  loadingOoo: 'Carregando...',
  selectAll: 'Selecionar Tudo',
  searchOoo: 'Buscar...',
  blanks: 'Em Branco',
  noMatches: 'Sem correspondências',
  filterOoo: 'Filtrar...',
  equals: 'Igual',
  notEqual: 'Diferente',
  lessThan: 'Menor que',
  greaterThan: 'Maior que',
  lessThanOrEqual: 'Menor ou igual',
  greaterThanOrEqual: 'Maior ou igual',
  inRange: 'No intervalo',
  contains: 'Contém',
  notContains: 'Não contém',
  startsWith: 'Começa com',
  endsWith: 'Termina com',
  columns: 'Colunas',
  filters: 'Filtros',
  noRowsToShow: 'Nenhum registro encontrado',
  pinColumn: 'Fixar Coluna',
  autosizeThiscolumn: 'Auto-dimensionar',
  autosizeAllColumns: 'Auto-dimensionar todas',
  resetColumns: 'Redefinir colunas',
  copy: 'Copiar',
  copyWithHeaders: 'Copiar com cabeçalhos',
  paste: 'Colar',
  export: 'Exportar',
  csvExport: 'Exportar CSV',
  excelExport: 'Exportar Excel',
};

// Density Control Component - extracted to avoid re-creation on every render
function DensityControl({ 
  densityConfig, 
  effectiveDensity, 
  globalDensity, 
  localDensityState, 
  onDensityChange 
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          data-testid="density-control-btn"
        >
          <AlignVerticalSpaceAround className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{densityConfig.label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
          Densidade (esta página)
        </div>
        {Object.entries(DENSITY_CONFIG).map(([key, config]) => (
          <DropdownMenuItem
            key={key}
            onClick={() => onDensityChange(key, false)}
            className="gap-2"
            data-testid={`density-${key}`}
          >
            {key === 'compact' && <Rows4 className="h-4 w-4" />}
            {key === 'normal' && <Rows3 className="h-4 w-4" />}
            {key === 'comfortable' && <AlignVerticalSpaceAround className="h-4 w-4" />}
            <span className="flex-1">{config.label}</span>
            {effectiveDensity === key && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        <div className="border-t my-1" />
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
          Densidade Global
        </div>
        {Object.entries(DENSITY_CONFIG).map(([key, config]) => (
          <DropdownMenuItem
            key={`global-${key}`}
            onClick={() => onDensityChange(key, true)}
            className="gap-2"
            data-testid={`density-global-${key}`}
          >
            {key === 'compact' && <Rows4 className="h-4 w-4" />}
            {key === 'normal' && <Rows3 className="h-4 w-4" />}
            {key === 'comfortable' && <AlignVerticalSpaceAround className="h-4 w-4" />}
            <span className="flex-1">{config.label}</span>
            {globalDensity === key && !localDensityState && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const DataGrid = forwardRef(function DataGrid({
  columnDefs,
  rowData,
  loading = false,
  pagination = true,
  paginationPageSize = 15,
  paginationPageSizeSelector = [10, 15, 25, 50, 100],
  onGridReady,
  onRowClick,
  onRowDoubleClick,
  height = 'auto',
  rowSelection = 'multiple',
  getRowId,
  enableSelection = false,
  // Header title
  title,
  // Density control
  showDensityControl = true,
  localDensity = null, // Override global density for this instance
  // Server-side props
  serverSide = false,
  totalItems = 0,
  pageNumber = 1,
  onPageChange,
  onSortChange,
  sortBy,
  sortDirection,
  ...props
}, ref) {
  const { t, i18n } = useTranslation();
  const gridRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [currentPageSize, setCurrentPageSize] = useState(paginationPageSize);
  
  // Density state - local override or global
  const { density: globalDensity, setDensity: setGlobalDensity } = useDataGridDensity();
  const [localDensityState, setLocalDensityState] = useState(localDensity);
  
  const effectiveDensity = localDensityState || globalDensity;
  const densityConfig = DENSITY_CONFIG[effectiveDensity] || DENSITY_CONFIG.normal;
  
  // Handle density change - can set local or global
  const handleDensityChange = (newDensity, isGlobal = false) => {
    if (isGlobal) {
      setGlobalDensity(newDensity);
      setLocalDensityState(null); // Clear local override
    } else {
      setLocalDensityState(newDensity);
    }
  };
  
  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    exportCSV: (onlySelected = false) => {
      if (gridRef.current?.api) {
        gridRef.current.api.exportDataAsCsv({
          onlySelected,
          fileName: `export_${new Date().toISOString().split('T')[0]}.csv`,
        });
      }
    },
    exportJSON: (onlySelected = false) => {
      if (!gridRef.current?.api) return null;
      
      let data;
      if (onlySelected) {
        data = gridRef.current.api.getSelectedRows();
      } else {
        data = [];
        gridRef.current.api.forEachNode(node => data.push(node.data));
      }
      return data;
    },
    getSelectedRows: () => {
      return gridRef.current?.api?.getSelectedRows() || [];
    },
    getSelectedCount: () => {
      return gridRef.current?.api?.getSelectedRows()?.length || 0;
    },
    getApi: () => gridRef.current?.api,
    deselectAll: () => {
      gridRef.current?.api?.deselectAll();
    },
  }));

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    return () => observer.disconnect();
  }, []);
  
  const isPortuguese = i18n.language === 'pt-BR';
  
  // Column defs with checkbox if selection enabled
  const processedColumnDefs = useMemo(() => {
    if (!enableSelection) return columnDefs;
    
    return [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        maxWidth: 50,
        suppressMenu: true,
        sortable: false,
        filter: false,
        resizable: false,
        pinned: 'left',
      },
      ...columnDefs,
    ];
  }, [columnDefs, enableSelection]);
  
  const defaultColDef = useMemo(() => ({
    sortable: !serverSide, // Disable client-side sorting if server-side
    filter: !serverSide,   // Disable client-side filtering if server-side
    resizable: true,
    minWidth: 100,
    flex: 1,
  }), [serverSide]);
  
  const localeText = useMemo(() => 
    isPortuguese ? AG_GRID_LOCALE_PT_BR : undefined
  , [isPortuguese]);
  
  const handleRowClicked = useCallback((event) => {
    if (onRowClick) onRowClick(event.data);
  }, [onRowClick]);
  
  const handleRowDoubleClicked = useCallback((event) => {
    if (onRowDoubleClick) onRowDoubleClick(event.data);
  }, [onRowDoubleClick]);
  
  const handleGridReady = useCallback((params) => {
    gridRef.current = params;
    if (onGridReady) onGridReady(params);
  }, [onGridReady]);

  // Server-side sort change handler
  const handleSortChanged = useCallback((event) => {
    if (!serverSide || !onSortChange) return;
    
    const sortModel = event.api.getColumnState()
      .filter(col => col.sort)
      .map(col => ({ colId: col.colId, sort: col.sort }));
    
    if (sortModel.length > 0) {
      onSortChange(sortModel[0].colId, sortModel[0].sort);
    } else {
      onSortChange(null, null);
    }
  }, [serverSide, onSortChange]);

  // Handle page navigation for server-side
  const handlePaginationChanged = useCallback((event) => {
    if (!serverSide || !onPageChange) return;
    
    const api = event.api;
    const newPage = api.paginationGetCurrentPage() + 1;
    const newPageSize = api.paginationGetPageSize();
    
    if (newPage !== currentPage || newPageSize !== currentPageSize) {
      setCurrentPage(newPage);
      setCurrentPageSize(newPageSize);
      onPageChange(newPage, newPageSize);
    }
  }, [serverSide, onPageChange, currentPage, currentPageSize]);
  
  const loadingOverlayComponent = useMemo(() => {
    return () => (
      <div className="ag-custom-loading">
        <div className="ag-custom-loading-spinner"></div>
        <span>{t('common.loading')}</span>
      </div>
    );
  }, [t]);
  
  const noRowsOverlayComponent = useMemo(() => {
    return () => (
      <div className="ag-custom-no-rows">
        <span>{t('common.noResults')}</span>
      </div>
    );
  }, [t]);

  // Dynamic height - now uses density config
  const gridHeight = useMemo(() => {
    if (height !== 'auto') return height;
    const rowCount = rowData?.length || 0;
    const rowH = densityConfig.rowHeight;
    const headerH = densityConfig.headerHeight;
    const paginationH = pagination ? 52 : 0;
    const minH = 250;
    const maxH = 550;
    
    const calculated = headerH + (Math.min(rowCount, paginationPageSize) * rowH) + paginationH;
    return `${Math.max(minH, Math.min(calculated, maxH))}px`;
  }, [rowData, height, pagination, paginationPageSize, densityConfig]);

  const themeClass = isDarkMode ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';
  
  // Density class for CSS styling
  const densityClass = `datagrid-density-${effectiveDensity}`;

  // Server-side pagination info
  const serverSidePaginationProps = serverSide ? {
    suppressPaginationPanel: false,
    paginationPageSize: currentPageSize,
    paginationPageSizeSelector: paginationPageSizeSelector,
  } : {};

  return (
    <div className={cn("datagrid-container", densityClass)}>
      {/* Grid Header with Title and Density Control */}
      {(title || showDensityControl) && (
        <div className="datagrid-header">
          <h3 className="datagrid-title">{title || ''}</h3>
          <div className="datagrid-header-actions">
            {showDensityControl && (
              <DensityControl 
                densityConfig={densityConfig}
                effectiveDensity={effectiveDensity}
                globalDensity={globalDensity}
                localDensityState={localDensityState}
                onDensityChange={handleDensityChange}
              />
            )}
          </div>
        </div>
      )}
      
      {/* Server-side pagination info */}
      {serverSide && totalItems > 0 && (
        <div className="datagrid-server-info">
          <span className="text-sm text-muted-foreground">
            Mostrando {((currentPage - 1) * currentPageSize) + 1} - {Math.min(currentPage * currentPageSize, totalItems)} de {totalItems} registros
          </span>
        </div>
      )}
      
      <div 
        className={`${themeClass} datagrid-wrapper`}
        style={{ height: gridHeight, width: '100%' }}
        data-theme={isDarkMode ? 'dark' : 'light'}
        data-density={effectiveDensity}
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={processedColumnDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          localeText={localeText}
          
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          {...serverSidePaginationProps}
          
          rowSelection={enableSelection ? rowSelection : undefined}
          rowHeight={densityConfig.rowHeight}
          headerHeight={densityConfig.headerHeight}
          
          onGridReady={handleGridReady}
          onRowClicked={handleRowClicked}
          onRowDoubleClicked={handleRowDoubleClicked}
          onSortChanged={handleSortChanged}
          onPaginationChanged={handlePaginationChanged}
          
          loading={loading}
          loadingOverlayComponent={loadingOverlayComponent}
          noRowsOverlayComponent={noRowsOverlayComponent}
          
          getRowId={getRowId}
          animateRows={true}
          suppressCellFocus={true}
          enableCellTextSelection={true}
          suppressRowClickSelection={!enableSelection}
          
          {...props}
        />
      </div>
    </div>
  );
});

export default DataGrid;
